import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { safeBrandingUrl } from './TenantBrand';
import '../styles/panel-theme.css';
import './customer-reference-shell.css';

function navigationTarget(value) {
  if (typeof value !== 'string' || !value || value.length > 2048 || /[\\\u0000-\u0020\u007f]/.test(value)) return null;
  if (/^\/(?!\/)/.test(value)) return { path: value, external: false };
  try {
    const url = new URL(value);
    return ['https:', 'http:', 'mailto:'].includes(url.protocol) && !url.username && !url.password
      ? { path: value, external: true } : null;
  } catch { return null; }
}

function ReferenceBrand({ name, logoUrl }) {
  const logo = safeBrandingUrl(logoUrl);
  const [failedImage, setFailedImage] = useState(null);
  const [frame, setFrame] = useState(null);
  const identity = `${name}:${logo}`;
  // Normalize transparent padding in same-origin uploaded logos for display.
  // The original asset remains unchanged; remote images use normal contain.
  const measureLogo = event => {
    const original = event.currentTarget;
    try {
      if (new URL(logo, window.location.href).origin !== window.location.origin) return;
      const scale = Math.min(1, 512 / Math.max(original.naturalWidth, original.naturalHeight));
      if (!Number.isFinite(scale) || !original.naturalWidth || !original.naturalHeight) return;
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(original.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(original.naturalHeight * scale));
      const context = canvas.getContext('2d'); if (!context) return;
      context.drawImage(original, 0, 0, canvas.width, canvas.height);
      const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
      let left = canvas.width, top = canvas.height, right = -1, bottom = -1;
      for (let y = 0; y < canvas.height; y += 1) for (let x = 0; x < canvas.width; x += 1) {
        if (pixels[(y * canvas.width + x) * 4 + 3] < 48) continue;
        left = Math.min(left, x); top = Math.min(top, y); right = Math.max(right, x); bottom = Math.max(bottom, y);
      }
      if (right < left || bottom < top || ((right - left) / canvas.width > .85 && (bottom - top) / canvas.height > .85)) return;
      const pad = 2; left = Math.max(0, left - pad); top = Math.max(0, top - pad);
      right = Math.min(canvas.width, right + pad + 1); bottom = Math.min(canvas.height, bottom + pad + 1);
      const sx = original.naturalWidth / canvas.width, sy = original.naturalHeight / canvas.height;
      setFrame({ identity, width: original.naturalWidth, height: original.naturalHeight,
        viewBox: `${left * sx} ${top * sy} ${(right - left) * sx} ${(bottom - top) * sy}` });
    } catch { /* CORS, canvas restrictions or decoding errors retain the original logo. */ }
  };
  if (logo && failedImage !== identity && frame?.identity === identity) return <svg role="img" aria-label={`${name} logo`}
    viewBox={frame.viewBox} preserveAspectRatio="xMinYMid meet">
    <image href={logo} width={frame.width} height={frame.height} onError={() => setFailedImage(identity)} />
  </svg>;
  return logo && failedImage !== identity
    ? <img src={logo} alt={`${name} logo`} onLoad={measureLogo} onError={() => setFailedImage(identity)} />
    : <span>{name}</span>;
}

// Presentation only: the caller supplies its resolved, authorized menu and
// real balance. This shell does not fetch data or add customer navigation.
export default function CustomerReferenceShell({
  brand = 'SMM Panel', logoUrl, brandPath = '/', navItems = [], balance, accountPath,
  onLogout, title, subtitle, actions, announcement, children, languageControl, wide = false,
  navigationLabel = 'Main navigation', menuLabel = 'Toggle navigation',
  skipLabel = 'Skip to content', accountLabel = 'Account', logoutLabel = 'Logout', balanceLabel = 'Balance',
}) {
  const { pathname, search } = useLocation();
  const navigationId = useId();
  const contentId = useId();
  const toggle = useRef(null);
  const content = useRef(null);
  const [mobile, setMobile] = useState(() => Boolean(window.matchMedia?.('(max-width: 1535px)').matches));
  const [open, setOpen] = useState(false);
  const brandTarget = navigationTarget(brandPath);
  const accountTarget = navigationTarget(accountPath);

  useEffect(() => {
    const query = window.matchMedia?.('(max-width: 1535px)');
    if (!query) return undefined;
    const update = () => { setMobile(query.matches); setOpen(false); };
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname, search]);
  useEffect(() => {
    if (!mobile || !open) return undefined;
    const dismiss = event => {
      if (event.key === 'Escape') { setOpen(false); toggle.current?.focus(); }
    };
    document.addEventListener('keydown', dismiss);
    return () => document.removeEventListener('keydown', dismiss);
  }, [mobile, open]);

  const close = () => setOpen(false);
  const link = (target, props, contents) => target.external
    ? <a {...props} href={target.path} rel="noopener noreferrer" onClick={close}>{contents}</a>
    : <Link {...props} to={target.path} onClick={close}>{contents}</Link>;
  const brandContent = <ReferenceBrand name={brand} logoUrl={logoUrl} />;

  return <div className="panel-shell customer-reference-shell" data-testid="customer-reference-shell">
    <a className="customer-reference-skip" href={`#${contentId}`} onClick={() => content.current?.focus()}>{skipLabel}</a>
    <header className="customer-reference-header">
      <div className="customer-reference-header-inner">
        {brandTarget ? link(brandTarget, { className: 'customer-reference-brand' }, brandContent)
          : <span className="customer-reference-brand">{brandContent}</span>}
        {languageControl && <div className="customer-reference-language">{languageControl}</div>}
        <button className="customer-reference-toggle" ref={toggle} type="button" hidden={!mobile}
          aria-label={menuLabel} aria-controls={navigationId} aria-expanded={open}
          onClick={() => setOpen(value => !value)}>
          <span aria-hidden="true"><span /><span /><span /></span>
        </button>
        <nav id={navigationId} className="customer-reference-navigation" aria-label={navigationLabel} hidden={mobile && !open}>
          <ul className="customer-reference-links">
            {navItems.map((item, index) => {
              const target = navigationTarget(item.path);
              if (!target) return null;
              const activePath = target.path.split(/[?#]/, 1)[0];
              const active = item.active !== undefined ? item.active : !target.external && (
                pathname === activePath || (!item.exact && activePath !== '/' && pathname.startsWith(`${activePath}/`)));
              return <li key={item.key || `${item.path}-${index}`}>
                {link(target, { className: `customer-reference-link${active ? ' is-active' : ''}`,
                  'aria-current': active ? 'page' : undefined }, item.label)}
              </li>;
            })}
          </ul>
          <div className="customer-reference-account">
            {balance !== undefined && balance !== null && <span className="customer-reference-balance" aria-label={`${balanceLabel}: ${balance}`}>{balance}</span>}
            {accountTarget && link(accountTarget, { className: 'customer-reference-link',
              'aria-current': !accountTarget.external && pathname === accountTarget.path ? 'page' : undefined }, accountLabel)}
            {typeof onLogout === 'function' && <button className="customer-reference-link" type="button" onClick={() => { close(); onLogout(); }}>{logoutLabel}</button>}
          </div>
        </nav>
      </div>
    </header>
    <main className={`customer-reference-content${wide ? ' customer-reference-content--wide' : ''}`} id={contentId} ref={content} tabIndex={-1}>
      {announcement && <div className="customer-reference-announcement">{announcement}</div>}
      {(title || subtitle || actions) && <div className="customer-reference-page-heading">
        <div>{title && <h1>{title}</h1>}{subtitle && <p>{subtitle}</p>}</div>
        {actions && <div className="customer-reference-page-actions">{actions}</div>}
      </div>}
      {children}
    </main>
  </div>;
}
